import axiosClient from '../../../api/axiosClient.js';
import { fetchPageData } from '../../../api/utils.js';

async function getGeneralInfo() {
  const { data } = await axiosClient.get(`/api/general-info/`);
  return data;
}

export const generalInfoQuery = () => {
  return {
    // Full-payload cache used by insights sub-pages (classified-multistep,
    // double-entry, principles) that read fields not present in the ?login
    // slim variant — sessions.level_names, for instance. This cache also
    // benefits from game-play pages that fetch the full general-info under
    // the same key.
    queryKey: ['generalInfo'],
    queryFn: async () => getGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

// Trimmed general-info payload for the Insights → Progress-Scores → Scores
// page. Backend early-exits on `?progress-scores` and returns only `games`
// with the four fields that page actually reads (id, game_type, name,
// is_active). Distinct queryKey so this cache stays separate from
// generalInfoQuery and doesn't cross-pollinate with pages that need the
// full payload.
async function getProgressScoresGeneralInfo() {
  const { data } = await axiosClient.get(`/api/general-info/?progress-scores`);
  return data;
}

export const generalInfoProgressScoresQuery = () => {
  return {
    queryKey: ['instructorGeneralInfo', 'progress-scores'],
    queryFn: async () => getProgressScoresGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

async function getConsolidatedScores({completed} = {}) {
  const url = `/api/scores/${completed ? '?completed=true' : ''}`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const consolidatedScoresQuery = ({completed} = {}) => {
  return {
    queryKey: ['consolidatedScoresInsights', { completed }],
    queryFn: async () => getConsolidatedScores({completed}),
    refetchOnWindowFocus: false,
    // 5-minute freshness window — navigating between sibling pages
    // (e.g. leaderboard points → accuracy → average-time) within this
    // window serves cached data instead of refetching on every mount.
    staleTime: 5 * 60 * 1000,
  };
};

// Aggregate variant — backend returns per-game-type summaries, per-
// attempt breakdowns, and unique_user_count in a single small payload.
// Used by the decision-insights/overall page so it doesn't have to
// flatten thousands of session rows just to render four bar values
// and a per-attempt line chart. Distinct cache key keeps the row-shape
// served from the existing consolidatedScoresQuery (consumed by the
// leaderboard pages) from getting confused with the aggregated shape.
async function getConsolidatedScoresAggregate({completed} = {}) {
  const params = new URLSearchParams();
  params.append('aggregate', 'true');
  if (completed) params.append('completed', 'true');
  const { data } = await axiosClient.get(`/api/scores/?${params.toString()}`);
  return data;
}

// Leaderboard variant — backend returns pre-computed top-10 users for
// three metrics (average_points_sessions, total_accuracy_session,
// average_time_session), each broken down by game type. Replaces the
// heavy full-session payload + frontend aggregation the leaderboard
// pages used to do.
async function getConsolidatedScoresLeaderboard() {
  const { data } = await axiosClient.get(`/api/scores/?leaderboard`);
  return data;
}

export const consolidatedScoresLeaderboardQuery = () => {
  return {
    queryKey: ['consolidatedScoresLeaderboard'],
    queryFn: async () => getConsolidatedScoresLeaderboard(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  };
};

export const consolidatedScoresAggregateQuery = ({completed} = {}) => {
  return {
    queryKey: ['consolidatedScoresAggregate', { completed }],
    queryFn: async () => getConsolidatedScoresAggregate({completed}),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  };
};

// Scoped to `?balancedequation` — the only consumer is the double-entry
// decision-insights page, which reads `.balanced_equation` off the
// response and ignores the other buckets. Backend trims the payload to
// just that bucket so we don't pay for classification / normal / etc.
// data we never render.
async function getConsolidatedScoresInsights({completed} = {}) {
  const params = ['balancedequation'];
  if (completed) params.push('completed=true');
  const { data } = await axiosClient.get(`/api/scores/insights/?${params.join('&')}`);
  return data;
}

export const consolidatedScoresInsightsQuery = ({completed} = {}) => {
  return {
    queryKey: ['consolidatedScoresInsightsData', 'balancedequation', { completed }],
    queryFn: async () => getConsolidatedScoresInsights({completed}),
    refetchOnWindowFocus: false
  };
};

// Classification + Normal Balance accuracy — backend returns pre-
// computed averages per category (elements, assets, liabilities, etc.)
// instead of the full per-session payload. Used by the classifying-
// normal decision-insights page.
async function getClassificationNormalAccuracy() {
  const { data } = await axiosClient.get(`/api/scores/insights/?classification_normal&average_accuracy`);
  return data;
}

export const classificationNormalAccuracyQuery = () => {
  return {
    queryKey: ['classificationNormalAccuracy'],
    queryFn: async () => getClassificationNormalAccuracy(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  };
};

async function getConsolidatedInsights() {
  const { data } = await axiosClient.get(`/api/insights/`);
  return data;
}

export const consolidatedInsightsQuery = () => {
  return {
    queryKey: ['consolidatedInsights'],
    queryFn: async () => getConsolidatedInsights(),
    refetchOnWindowFocus: false
  };
};

// Game-type-scoped variant — backend narrows the response to a single
// game type so we don't pull every type's payload on pages that only
// need one. Cache key includes gameType to keep it from colliding with
// the unscoped consolidatedInsightsQuery.
async function getConsolidatedInsightsByGameType({gameType}) {
  const { data } = await axiosClient.get(`/api/insights/?game_type=${gameType}`);
  return data;
}

export const consolidatedInsightsByGameTypeQuery = ({gameType}) => {
  return {
    queryKey: ['consolidatedInsightsByGameType', gameType],
    queryFn: async () => getConsolidatedInsightsByGameType({gameType}),
    refetchOnWindowFocus: false,
    enabled: !!gameType,
  };
};

// Lightweight courses lookup — used by the progress-scores pages to
// resolve the instructor's course id before firing the aggregated
// progress-scores endpoint. Cached across siblings via the shared
// ['courses'] key so navigating between the two pages doesn't re-fetch.
async function getCourses() {
  return fetchPageData(`/api/users/courses/`);
}

export const coursesQuery = () => {
  return {
    queryKey: ['courses'],
    queryFn: async () => getCourses(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  };
};

// Dedicated endpoint for /instructor/insights/progress-scores/{progress,scores}.
// Returns { games, students[] } with each student carrying a per_game map of
// { status, best_accuracy }. Replaces the old fan-out of courseMembersQuery
// + gamesQuery + gamesSessionsQuery + consolidatedScoresQuery that made ~10
// requests to compute the same table client-side. Single call, pre-aggregated.
async function getProgressScores({courseId}) {
  const { data } = await axiosClient.get(`/api/instructor/progress-scores/?course=${courseId}`);
  return data;
}

export const progressScoresQuery = ({courseId} = {}) => {
  return {
    queryKey: ['progressScores', courseId ?? null],
    queryFn: async () => getProgressScores({courseId}),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!courseId,
  };
};

// Level-breakdown variant of the progress-scores endpoint. Same
// {games, students[]} shape, but each per_game entry carries
// best_accuracy_level1 / _level2 / _level3 (nullable) instead of the
// aggregate best_accuracy. Consumed lazily by the Scores page — the
// instructor Scores CSV picker triggers this fetch only when the user
// asks to download the level-scores CSV, so the extra request stays
// off the initial render path.
async function getProgressScoresLevels({courseId}) {
  const { data } = await axiosClient.get(`/api/instructor/progress-scores/?course=${courseId}&level-scores`);
  return data;
}

export const progressScoresLevelsQuery = ({courseId} = {}) => {
  return {
    queryKey: ['progressScoresLevels', courseId ?? null],
    queryFn: async () => getProgressScoresLevels({courseId}),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    // Callers gate this with their own `enabled` (e.g. only true
    // once the user clicks the level-scores download button) so the
    // request stays lazy.
  };
};

export async function getGames() {
  return fetchPageData(`/api/games/games/`);
}

export const gamesQuery = () => {
  return {
    queryKey: ['games'],
    queryFn: async () => getGames(),
    refetchOnWindowFocus: false,
    // Games rarely change within a session — keep cached for 5 minutes
    // so sibling pages don't refetch on every mount.
    staleTime: 5 * 60 * 1000,
  };
};

export async function getClassificationScores({gameId, completed}) {
  return fetchPageData(`/api/classification/scores/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export const classificationScoresQuery = (gameId, completed) => {
  return {
    queryKey: ['classificationScoresInsights'],
    queryFn: async () => getClassificationScores({gameId, completed}),
    refetchOnWindowFocus: false,
    retry: false,
  }
};

export async function getNormalScores({gameId, completed}) {
  return fetchPageData(`/api/normal/scores/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export const normalScoresQuery = (gameId, completed) => {
  return {
    queryKey: ['normalScoresInsights'],
    queryFn: async () => getNormalScores({gameId, completed}),
    refetchOnWindowFocus: false,
    retry: false,
  }
};

export async function getPrinciplesScores({gameId, completed}) {
  return fetchPageData(`/api/principles/scores/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export const principlesScoresQuery = (gameId, completed) => {
  return {
    queryKey: ['principlesScoresInsights'],
    queryFn: async () => getPrinciplesScores({gameId, completed}),
    refetchOnWindowFocus: false,
    retry: false,
  }
};

export async function getBalancedEquationScores({gameId, completed}) {
  return fetchPageData(`/api/equation/scores/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export const balancedEquationScoresQuery = (gameId, completed) => {
  return {
    queryKey: ['balancedEquationScoresInsights'],
    queryFn: async () => getBalancedEquationScores({gameId, completed}),
    retry: false,
    refetchOnWindowFocus: false
  }
};

export async function getClassificationScoresByQuestion(gameId) {
  return fetchPageData(`/api/classification/scores/by-question/${gameId ? `?game=${gameId}` : ''}`);
}

// Aggregate variant (?aggregate=true) — backend collapses the per-
// session-per-question rows into one row per account_item, with
// average_accuracy / average_time / session_count already computed.
// Response is unpaginated and bounded by the number of distinct
// accounts in the course's scenarios, so the leaderboard pages no
// longer need to drain hundreds of pages just to compute ~30 averages.
export async function getClassificationScoresByQuestionAggregate(gameId) {
  const url = gameId
    ? `/api/classification/scores/by-question/?aggregate=true&game=${gameId}`
    : `/api/classification/scores/by-question/?aggregate=true`;
  const response = await axiosClient.get(url);
  return response?.data;
}

export async function getNormalScoresByQuestion(gameId) {
  return fetchPageData(`/api/normal/scores/by-question/${gameId ? `?game=${gameId}` : ''}`);
}

// Aggregate variant — same shape as Classification's aggregate
// (question_name + account_item_id + average_accuracy + average_time
// + session_count, unpaginated, ~30 rows).
export async function getNormalScoresByQuestionAggregate(gameId) {
  const url = gameId
    ? `/api/normal/scores/by-question/?aggregate=true&game=${gameId}`
    : `/api/normal/scores/by-question/?aggregate=true`;
  const response = await axiosClient.get(url);
  return response?.data;
}

export async function getPrinciplesScoresByQuestion(gameId) {
  const response = await axiosClient.get(`/api/principles/scores/principles/${gameId ? `?game=${gameId}` : ''}`);
  return response?.data;
}

// Aggregate variant — Principles rows key off principle_name /
// principle_id rather than question_name / account_item_id, mirroring
// the legacy endpoint's row shape.
export async function getPrinciplesScoresByQuestionAggregate(gameId) {
  const url = gameId
    ? `/api/principles/scores/principles/?aggregate=true&game=${gameId}`
    : `/api/principles/scores/principles/?aggregate=true`;
  const response = await axiosClient.get(url);
  return response?.data;
}

export async function getBalancedEquationScoresByQuestion(gameId) {
  return fetchPageData(`/api/equation/scores/by-question/${gameId ? `?game=${gameId}` : ''}`);
}

export async function getEquationScoresInsights({gameId, completed}) {
  return fetchPageData(`/api/equation/scores/insights/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export async function getPrinciplesScoresPrinciples({gameId, completed}) {
  const response = await axiosClient.get(`/api/principles/scores/principles/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
  return response?.data;
}

// Cross-game variant of /api/principles/scores/insights — omits the
// `?game=` filter so the response covers every Principles game in the
// instructor's course. Used by the instructor decision-insights page
// to average accuracy per Assumption/Principle/Constraint across all
// games and sessions.
export async function getPrinciplesScoresItemInsights() {
  return fetchPageData(`/api/principles/scores/insights/`);
}

export const principlesScoresItemInsightsQuery = () => {
  return {
    queryKey: ['principlesScoresItemInsights'],
    queryFn: async () => getPrinciplesScoresItemInsights(),
    refetchOnWindowFocus: false,
  };
};

export async function getClassificationScoresInsights({gameId, completed}) {
  return fetchPageData(`/api/classification/scores/insights/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export async function getNormalScoresInsights({gameId, completed}) {
  return fetchPageData(`/api/normal/scores/insights/${gameId ? `?game=${gameId}` : ''}${completed ? `?completed=true` : ''}`);
}

export async function getScenarioLevels(scenarioId) {
  return fetchPageData(`/api/games/scenario-levels/?scenarios=${scenarioId}`);
}

export async function getCourseMembers() {
  return fetchPageData(`/api/users/course-members/`);
}

export const courseMembersQuery = () => {
  return {
    queryKey: ['courseMembers'],
    queryFn: async () => getCourseMembers(),
    refetchOnWindowFocus: false,
    // Course roster rarely changes within an instructor session —
    // cache for 5 minutes so navigating between sibling leaderboard
    // pages doesn't refetch every time.
    staleTime: 5 * 60 * 1000,
  };
};

// Lightweight student-count endpoint. Use this whenever a page only
// needs to display "N students" rather than the full roster — the
// full /course-members/ list paginates and can be hundreds of rows.
// The response shape varies slightly between deployments; the helper
// reads the first key it recognizes.
export async function getStudentCount() {
  const { data } = await axiosClient.get(`/api/users/course-members/counts/`);
  return (
    data?.students ??
    data?.student_count ??
    data?.count ??
    0
  );
}

export const studentCountQuery = () => {
  return {
    queryKey: ['studentCount'],
    queryFn: async () => getStudentCount(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  };
};

// Slim roster — backend strips every field except user_id + name.
// The leaderboard pages only ever read those two, so this skips the
// hundreds of KB of full course-member objects (with email, course,
// is_student, view_welcome, etc.) the legacy paginated endpoint
// returned. Response is unpaginated and a flat array.
export async function getCourseMembersStudentNames() {
  const { data } = await axiosClient.get(`/api/users/course-members/?student_name=all`);
  return data;
}

export const courseMembersStudentNamesQuery = () => {
  return {
    queryKey: ['courseMembersStudentNames'],
    queryFn: async () => getCourseMembersStudentNames(),
    refetchOnWindowFocus: false,
    // Roster rarely changes within an instructor session — keep
    // cached for 5 min so navigating between leaderboard pages
    // doesn't refetch every time.
    staleTime: 5 * 60 * 1000,
  };
};

export async function getClassificationDecisionsInsights({gameId}) {
  const response = await axiosClient.get(`/api/classification/decisions/insights/${gameId}/`);
  return response?.data;
}

export async function getBalancedEquationDecisionsInsights({gameId}) {
  const response = await axiosClient.get(`/api/equation/decisions/insights/${gameId}/`);
  return response?.data;
}

export async function getPrinciplesDecisionsInsights({gameId}) {
  const response = await axiosClient.get(`/api/principles/decisions/insights/${gameId}/`);
  return response?.data;
}

export async function getGamesTransactions() {
  return fetchPageData(`/api/games/transactions/`);
}

export async function getGamesTransaction({id}) {
  const response = await axiosClient.get(`/api/games/transactions/${id}/`);
  return response;
}


export async function getNormalDecisionsInsights({gameId}) {
  const response = await axiosClient.get(`/api/normal/decisions/insights/${gameId}`);
  return response?.data;
}

export async function getGamesSessions() {
  return fetchPageData(`/api/games/sessions/`);
}

export const gamesSessionsQuery = () => {
  return {
    queryKey: ['gamesSessionsScores'],
    queryFn: async () => getGamesSessions(),
    refetchOnWindowFocus: false
  };
};

export async function getBalancedEquationDecisions() {
  return fetchPageData('/api/equation/decisions/');
}

export const balancedEquationDecisionsQuery = () => {
  return {
    queryKey: [`balancedEquationDecisionsResponse`],
    queryFn: async () => getBalancedEquationDecisions(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getPrinciplesDecisions() {
  return fetchPageData('/api/principles/decisions/');
}

export const principlesDecisionsQuery = () => {
  return {
    queryKey: [`principlesDecisionsResponse`],
    queryFn: async () => getPrinciplesDecisions(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getItems() {
  return fetchPageData(`/api/games/items/`);
}

export const itemsQuery = () => {
  return {
    queryKey: ['items'],
    queryFn: async () => getItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};